import { httpsCallable } from 'firebase/functions';
import { appId, functions } from '@/lib/firebase';

export type ProductGroup = 'va_services' | 'saas_subscriptions' | 'one_time_services';
export type StripeMode = 'test' | 'live';
export type BillingType = 'one_time' | 'monthly' | '6_month' | 'yearly';
export type DonationFrequency = 'one_time' | 'monthly';

export type DonorWallConsentLevel = 'anonymous' | 'firstNameOnly' | 'full';

export interface CheckoutAttributionMetadata {
  campaignId?: string;
  campaignName?: string;
  ambassadorId?: string;
  ambassadorName?: string;
  sourcePage?: string;
  donationFrequency?: DonationFrequency;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  donorWallConsent?: DonorWallConsentLevel;
  donorCountry?: string;
}

export interface StripePriceFormInput {
  unitAmount: number;
  currency?: string;
  billingType: BillingType;
  nickname?: string;
}

export interface StripeConfigPayload {
  publishableKey: string;
  secretKey: string;
  webhookSecret?: string;
  mode: StripeMode;
}

export interface StripeProductPayload {
  name: string;
  description?: string;
  group: ProductGroup;
  visible?: boolean;
  displayOrder?: number;
  price: StripePriceFormInput;
}

export interface StripeProductUpdatePayload {
  productId: string;
  name?: string;
  description?: string;
  group?: ProductGroup;
  visible?: boolean;
  displayOrder?: number;
  active?: boolean;
  price?: StripePriceFormInput;
}

export async function saveStripeConfig(payload: StripeConfigPayload) {
  const call = httpsCallable(functions, 'saveStripeConfig');
  const result = await call({ appId, ...payload });
  return result.data as {
    success: boolean;
    mode: StripeMode;
    publishableKeyMasked: string;
    secretKeyMasked: string;
    webhookSecretMasked: string;
  };
}

export async function syncStripeProducts() {
  const call = httpsCallable(functions, 'syncStripeProducts');
  const result = await call({ appId });
  return result.data as { success: boolean; synced: number };
}

export async function createStripeProduct(payload: StripeProductPayload) {
  const call = httpsCallable(functions, 'createStripeProduct');
  const result = await call({ appId, ...payload });
  return result.data as { success: boolean; productId: string; priceId: string };
}

export async function updateStripeProduct(payload: StripeProductUpdatePayload) {
  const call = httpsCallable(functions, 'updateStripeProduct');
  const result = await call({ appId, ...payload });
  return result.data as { success: boolean };
}

export async function archiveStripeProduct(productId: string) {
  const call = httpsCallable(functions, 'archiveStripeProduct');
  const result = await call({ appId, productId });
  return result.data as { success: boolean };
}

export async function createCheckoutSession(
  priceId: string,
  successUrl: string,
  cancelUrl: string,
  metadata?: CheckoutAttributionMetadata
) {
  const call = httpsCallable(functions, 'createCheckoutSession');
  const result = await call({
    appId,
    priceId,
    successUrl,
    cancelUrl,
    metadata: metadata ?? {},
  });
  return result.data as { success: boolean; sessionUrl: string };
}

export interface DonationCheckoutPayload {
  amount: number;
  currency?: string;
  donationFrequency?: DonationFrequency;
  successUrl: string;
  cancelUrl: string;
  donorEmail?: string;
  donorName?: string;
  metadata?: CheckoutAttributionMetadata;
}

export async function createDonationCheckoutSession(payload: DonationCheckoutPayload) {
  const call = httpsCallable(functions, 'createDonationCheckoutSession');
  const donorEmail = payload.donorEmail?.trim();
  const donorName = payload.donorName?.trim();
  const result = await call({
    appId,
    amount: payload.amount,
    currency: payload.currency ?? 'usd',
    donationFrequency: payload.donationFrequency ?? 'one_time',
    successUrl: payload.successUrl,
    cancelUrl: payload.cancelUrl,
    ...(donorEmail ? { donorEmail } : {}),
    ...(donorName ? { donorName } : {}),
    metadata: payload.metadata ?? {},
  });

  return result.data as {
    success: boolean;
    sessionUrl: string;
    sessionId: string;
  };
}

export async function createBillingPortalSession(returnUrl: string) {
  const call = httpsCallable(functions, 'createBillingPortalSession');
  const result = await call({ appId, returnUrl });
  return result.data as {
    success: boolean;
    portalUrl: string;
  };
}

export async function getCheckoutSessionStatus(sessionId: string) {
  const call = httpsCallable(functions, 'getCheckoutSessionStatus');
  const result = await call({ appId, sessionId });
  return result.data as {
    success: boolean;
    sessionId: string;
    mode: 'payment' | 'subscription' | 'setup' | null;
    paymentStatus: 'paid' | 'unpaid' | 'no_payment_required';
    amountTotal: number;
    currency: string;
    customerEmail: string | null;
    donorIdentityStatus: 'guest_unclaimed' | 'claimed';
    donorClaimReference: string | null;
    stripeSubscriptionId: string | null;
    donationFrequency: DonationFrequency;
    warriorNumber: number | null;
    monthlyWarriorCount: number;
  };
}

export async function reconcileDonorIdentity(email?: string) {
  const call = httpsCallable(functions, 'reconcileDonorIdentity');
  const result = await call({ appId, email: email ?? '' });
  return result.data as {
    success: boolean;
    uid: string;
    normalizedEmail: string;
    donorsLinked: number;
    donationsLinked: number;
    invoiceLinks: number;
    rollupsMirrored: number;
  };
}
